import Highcharts, { TooltipFormatterContextObject } from 'highcharts';

import { formatPercentage } from 'components/insights/formatting';
import { colors } from 'styles/theme/colors';
import textStyles from 'styles/theme/textStyles';

import { type ScrollConfig } from '../../networkPerformance/slideout/headers/chartHelpers';

// Registers the shared `column2` overlaid-column series type used below.
import 'components/shared/charts/column2Series';

const POINT_WIDTH = 40;

// Rough tooltip width, used only to pick which side of the bar to render on — the exact width isn't
// known until after the label is built, and the positioner and formatter must agree on the side.
const TOOLTIP_FLIP_WIDTH = 160;

// Not enough room to the right of the bar (near the chart's right edge) → render on the left instead.
const shouldFlipLeft = (plotX: number, plotWidth: number): boolean =>
  plotWidth - plotX < TOOLTIP_FLIP_WIDTH + POINT_WIDTH;

// The fixed chart height (px). Exported so the header can reserve it in MetricDetailLayout's
// loading/empty placeholder — keeps the card from collapsing and jumping when data lands.
export const OVERLAID_COLUMN_CHART_HEIGHT = 280;

type Series = { name: string; values: ReadonlyArray<number | null> };

type OverlaidColumnChartArgs = {
  // Entity names for the x-axis (index-aligned with the series + rates).
  names: ReadonlyArray<string | null>;
  // Rate per entity — drives both the descending sort and the data label on top of each bar.
  rates: ReadonlyArray<number | null>;
  // Grey base column (the total) with the colored overlay column drawn in front of it.
  baseSeries: Series;
  overlaySeries: Series & { color: string };
};

// The "grey total column + colored overlay column, rate label on top, sorted by rate desc" chart
// shared by the Accepted / Selected / Admitted drills. They differ only in which three values they
// read and the overlay color/labels — everything else (layout, sort, data labels, scroll) is
// identical, so it lives here instead of being copy-pasted per header.
export function buildOverlaidColumnChart({ names, rates, baseSeries, overlaySeries }: OverlaidColumnChartArgs): {
  chartOptions: Highcharts.Options;
  scrollConfig: ScrollConfig;
  isEmpty: boolean;
} {
  // Drop entities with no volume in the base (denominator) column — e.g. a referral/payer source
  // that received referrals but had none reach an outcome. Those render as an empty bar with an
  // em-dash rate, so they're excluded rather than shown as noise.
  const order = names
    .map((_, i) => i)
    .filter((i) => (baseSeries.values[i] ?? 0) > 0)
    .sort((a, b) => (rates[b] ?? 0) - (rates[a] ?? 0));
  const sortedNames = order.map((i) => names[i] || 'None');
  const sortedBase = order.map((i) => baseSeries.values[i] ?? 0);
  const sortedOverlay = order.map((i) => overlaySeries.values[i] ?? 0);
  const sortedRates = order.map((i) => rates[i] ?? null);

  const chartOptions: Highcharts.Options = {
    chart: { type: 'column', style: { fontFamily: textStyles.base.fontFamily }, height: OVERLAID_COLUMN_CHART_HEIGHT },
    accessibility: { enabled: false },
    credits: { enabled: false },
    title: { text: undefined },
    legend: {
      align: 'center',
      verticalAlign: 'bottom',
      symbolRadius: 6,
      itemStyle: { fontSize: '12px', fontWeight: 'normal', color: colors.black75 },
    },
    xAxis: {
      categories: sortedNames,
      lineColor: colors.black10,
      tickLength: 0,
      labels: { style: { fontSize: '11px', color: colors.black75 } },
    },
    yAxis: {
      title: { text: undefined },
      gridLineColor: colors.black10,
      labels: { style: { fontSize: '11px', color: colors.black50 } },
    },
    // Shared dark tooltip matching the Figma design: one row per series — a rounded color swatch,
    // then the count in bold, then the (short) series label. No entity header or rate row.
    tooltip: {
      useHTML: true,
      // Shared, so the whole stacked bar (both overlaid series at a category) is one hover target.
      shared: true,
      // The dark box + left pointer are drawn in the HTML below (Highcharts' own callout doesn't
      // render reliably with useHTML), so hide Highcharts' box chrome.
      backgroundColor: 'transparent',
      borderWidth: 0,
      shadow: false,
      padding: 0,
      // Sit to the right of the bar (vertically centered), so the HTML pointer on the tooltip's left
      // edge points back at it — but flip to the left when the bar is too close to the right edge.
      positioner: function (
        this: Highcharts.Tooltip,
        labelWidth: number,
        labelHeight: number,
        point: Highcharts.TooltipPositionerPointObject
      ) {
        const gap = POINT_WIDTH / 2 + 12;
        const rawX = shouldFlipLeft(point.plotX, this.chart.plotWidth)
          ? this.chart.plotLeft + point.plotX - gap - labelWidth
          : this.chart.plotLeft + point.plotX + gap;
        const rawY = this.chart.plotTop + point.plotY - labelHeight / 2;
        // Keep the tooltip within the chart so a tall bar's tooltip isn't clipped at the top/edges.
        return {
          x: Math.max(0, Math.min(rawX, this.chart.chartWidth - labelWidth)),
          y: Math.max(0, Math.min(rawY, this.chart.chartHeight - labelHeight)),
        };
      },
      formatter: function (this: TooltipFormatterContextObject): string {
        const rows = (this.points ?? [])
          .map((p) => {
            // The base "total" column is a translucent grey (black10) that disappears on the dark
            // tooltip, so give it a light swatch; the overlay keeps its own (solid) color.
            const swatchColor = p.series.name === overlaySeries.name ? (p.color as string) : colors.white75;
            const swatch =
              `<span style="display:inline-block;width:10px;height:10px;border-radius:2px;` +
              `background:${swatchColor};margin-right:8px;vertical-align:middle;"></span>`;
            // Series names read "Total Received / Total Accepted"; the tooltip shows the short noun.
            const label = p.series.name.replace(/^Total /, '');
            return `<span style="white-space:nowrap;">${swatch}<b>${p.y}</b> ${label}</span>`;
          })
          .join('<br/>');
        // The pointer sits on the edge facing the bar: left edge (points left) normally, right edge
        // (points right) when the tooltip has flipped to the left of the bar. Same side decision the
        // positioner makes, from the bar's x-position.
        const anchor = this.points?.[0];
        const plotX = (anchor?.point as Highcharts.Point | undefined)?.plotX;
        const plotWidth = anchor?.series?.chart?.plotWidth;
        const flipped = plotX != null && plotWidth != null && shouldFlipLeft(plotX, plotWidth);
        const pointer = flipped
          ? `<span style="position:absolute;right:-6px;top:50%;transform:translateY(-50%);width:0;height:0;` +
            `border-top:6px solid transparent;border-bottom:6px solid transparent;border-left:6px solid ${colors.black75};"></span>`
          : `<span style="position:absolute;left:-6px;top:50%;transform:translateY(-50%);width:0;height:0;` +
            `border-top:6px solid transparent;border-bottom:6px solid transparent;border-right:6px solid ${colors.black75};"></span>`;
        return (
          `<div style="position:relative;background:${colors.black75};color:${colors.white};` +
          `border-radius:8px;padding:10px 12px;font-size:12px;line-height:1.6;">${pointer}${rows}</div>`
        );
      },
    },
    plotOptions: {
      column: {
        borderRadius: { radius: 3, scope: 'point' as const, where: 'end' as const },
        pointWidth: POINT_WIDTH,
      },
      // @ts-expect-error column2 is our custom side-by-side series type, not on the plotOptions type
      column2: {
        borderRadius: { radius: 3, scope: 'point' as const, where: 'end' as const },
        pointWidth: POINT_WIDTH,
      },
      series: { animation: false, inactiveOtherPoints: true, events: { legendItemClick: () => false } },
    },
    series: [
      {
        type: 'column',
        name: baseSeries.name,
        color: colors.black10,
        data: sortedBase.map((y, i) => ({
          y,
          label: formatPercentage(sortedRates[i]),
          dataLabels: {
            enabled: true,
            formatter: function (this: TooltipFormatterContextObject): string {
              return `<span style="font-size:10px;font-weight:normal;">${this.point.label}</span>`;
            },
          },
        })),
      },
      { type: 'column2' as 'column', name: overlaySeries.name, color: overlaySeries.color, data: sortedOverlay },
    ],
  };

  const scrollConfig: ScrollConfig = { minWidth: (POINT_WIDTH + POINT_WIDTH * 1.5) * sortedNames.length };
  return { chartOptions, scrollConfig, isEmpty: sortedNames.length === 0 };
}
